import { Box, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React, { useState } from 'react';
import axiosService from 'utils/axios';
import { Person } from 'types/entities/types';
import ModalPopup from '../../../components/Modal/ModalPopup';

interface Props {
  person?: Person;
  personName?: string;
  onSave: (person: Person) => void;
  modalTitle: string;
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}

interface PersonCreateUpdateError {
  error: string;
}

export default function AddOrEditPeople(props: Props) {
  const { person, personName = '', onSave, modalTitle, isModalOpen, setModalOpen } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<PersonCreateUpdateError | null>();

  const [firstNameInput, setFirstNameInput] = useState(person?.first_name || personName);
  const [lastNameInput, setLastNameInput] = useState(person?.last_name || '');
  const [emailInput, setEmailInput] = useState(person?.email || '');
  const [showInvalidEmailError, setShowInvalidEmailError] = useState(false);
  const [phoneInput, setPhoneInput] = useState(person?.phone || '');
  const [addressInput, setAddressInput] = useState(person?.address || '');
  const [eoriInput, setEoriInput] = useState(person?.eori || '');
  const [pinInput, setPinInput] = useState(person?.pin || '');

  const handleSubmit = () => {
    setLoading(true);
    const data = {
      // eslint-disable-next-line camelcase
      first_name: firstNameInput,
      // eslint-disable-next-line camelcase
      last_name: lastNameInput,
      email: emailInput,
      phone: phoneInput,
      address: addressInput,
      eori: eoriInput,
      pin: pinInput,
    };
    if (person) {
      // Update existing person
      axiosService
        .patch(`/entities/person/${person.id}/`, data)
        .then(personData => {
          setLoading(false);
          setError(null);
          onSave(personData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    } else {
      // Create new person
      axiosService
        .post('/entities/person/', data)
        .then(personData => {
          setLoading(false);
          setError(null);
          onSave(personData.data);
        })
        .catch(err => {
          setLoading(false);
          setError(err.response.data || {});
        });
    }
  };

  return (
    <ModalPopup
      title={modalTitle}
      open={isModalOpen}
      onClose={() => setModalOpen(false)}
      actionButtons={
        <LoadingButton
          loading={loading}
          onClick={handleSubmit}
          type="submit"
          fullWidth
          variant="contained"
          disabled={!firstNameInput || showInvalidEmailError}
        >
          {person ? 'Edit person' : 'Create person'}
        </LoadingButton>
      }
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ width: '100%', mt: 1 }}>
          {error ? <Typography>{error.error}</Typography> : null}
          <TextField
            required
            fullWidth
            margin="normal"
            id="first-name"
            label="First name"
            autoFocus
            value={firstNameInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFirstNameInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="last-name"
            label="Last name"
            value={lastNameInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setLastNameInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="email"
            label="Email"
            type="email"
            value={emailInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const email = event.target.value;
              setEmailInput(email);
              if (
                !!email &&
                // eslint-disable-next-line
                !/^(mailto:)?([\u0030-\u0039\u0041-\u005a\u0061-\u007a\u0400-\u04FF\u0391-\u03CE\u0080-\u00FF\u0150\u0151\u0170\u0171\u04F2\u04F3\-\_\.\%\+])+\@([\u0030-\u0039\u0041-\u005a\u0061-\u007a\u0400-\u04FF\u0391-\u03CE\u0080-\u00FF\u0150\u0151\u0170\u0171\u04F2\u04F3\-\_\.]+)\.([\u0030-\u0039\u0041-\u005a\u0061-\u007a\u0400-\u04FF\u0391-\u03CE\u0080-\u00FF\u0150\u0151\u0170\u0171\u04F2\u04F3]+)$|^\$email\$$/i.test(
                  email
                )
              ) {
                setShowInvalidEmailError(true);
              } else {
                setShowInvalidEmailError(false);
              }
            }}
            error={showInvalidEmailError}
            helperText={showInvalidEmailError ? 'Please enter a valid email address.' : ''}
          />
          <TextField
            fullWidth
            margin="normal"
            id="phone"
            label="Phone"
            type="tel"
            value={phoneInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setPhoneInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="address"
            label="Address"
            value={addressInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setAddressInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="pin"
            label="PIN"
            value={pinInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setPinInput(event.target.value);
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            id="eori"
            label="EORI"
            value={eoriInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setEoriInput(event.target.value);
            }}
          />
        </Box>
      </Box>
    </ModalPopup>
  );
}
